import { z } from 'zod';
import { UuidSchema, IsoDateTimeSchema } from '../common/ids.js';
import { TaskSchema } from './task.js';
/**
 * `GET /me/tasks` — the tasks on the caller's current or next shift (T-14).
 *
 * A plain array, like `MeShiftsResponseSchema`: an empty one is the honest
 * answer to "no shift, so no tasks and no punch-out gate" (T-17), and the PWA
 * already holds the shift itself from `GET /me/context`.
 *
 * Deliberately the full `TaskSchema` and not a narrowed worker shape (unlike
 * `MePunchSchema`): a task carries no location trace, device fingerprint or
 * clock telemetry — it is an instruction written *to* this worker, plus their
 * own statement back. There is nothing on it to withhold from them.
 */
export const MyTasksResponseSchema = z.array(TaskSchema);
/** 15 MB. Photos upload at native resolution / q0.92 since 2026-09-04 (owner
 * request) — a busy 12 MP scene lands at 2–8 MB — and the production edge caps
 * the PUT at 20 MB (docker/Caddyfile), so this stays inside that with margin. */
export const TASK_PHOTO_MAX_BYTES = 15 * 1024 * 1024;
/**
 * `POST /me/tasks/:id/photos` — asks for a presigned PUT; the bytes never
 * touch the API (T-11).
 *
 * `contentType` is `image/jpeg` only: the PWA canvas-re-encodes every capture
 * to JPEG before upload, which is also what strips EXIF (including GPS). A
 * second accepted type would be a path around that. Both fields are enforced
 * on the presigned PUT (H-1): `sizeBytes` via `ContentLength` and
 * `contentType` via `content-type`, both listed in `SignedHeaders`
 * (`storage.service.ts` `presignPut`) — a client that lies about either
 * cannot PUT. The presigned GET forces the read back out as `image/jpeg`
 * with `Content-Disposition: attachment` (`presignGet`), so a stored object
 * can never render as anything else on this origin.
 */
export const CreateTaskPhotoRequestSchema = z.object({
    contentType: z.literal('image/jpeg'),
    sizeBytes: z.int().min(1).max(TASK_PHOTO_MAX_BYTES),
});
export const CreateTaskPhotoResponseSchema = z.object({
    photoId: UuidSchema,
    putUrl: z.url(),
    expiresAt: IsoDateTimeSchema,
});
/**
 * `POST /me/tasks/:id/complete` → `done`.
 *
 * `photoIds` is `min(0)`: a task with `photo_required = false` legitimately
 * completes with none, and only the server knows that flag. The 1–3 bound
 * (T-12) is the upper limit; the *lower* one is `TASK_PHOTO_REQUIRED`, a
 * business refusal, not a shape error.
 */
export const CompleteTaskRequestSchema = z.object({
    justification: z.string().trim().min(10).max(2000),
    photoIds: z.array(UuidSchema).max(3),
});
/** `POST /me/tasks/:id/unable` → `unable`. Never takes a photo (T-06). */
export const UnableTaskRequestSchema = z.object({
    justification: z.string().trim().min(10).max(2000),
});
//# sourceMappingURL=me-tasks.js.map